/**
 * InlinePayment — Stripe Payment Element wrapper.
 *
 * Replaces the redirect-to-Stripe-hosted-page flow with an embedded payment
 * form that keeps the customer on our branded page. Stripe surfaces Apple Pay,
 * Google Pay, and card fields automatically based on the customer's device
 * (controlled by `automatic_payment_methods: {enabled: true}` on the backend
 * PaymentIntent).
 *
 * Props:
 *   clientSecret  PaymentIntent client_secret returned by the backend.
 *   orderToken    Used to build the `return_url` Stripe redirects to after
 *                 successful 3DS / wallet flows.
 *   amount        Numeric amount for the button label (e.g. 199).
 *   currency      "AED" / "USD" — display label only, Stripe charges in the
 *                 currency baked into the PaymentIntent.
 *   currencyLabel Optional Arabic unit (e.g. "درهم"). Defaults to currency.
 *   returnPath    The path Stripe redirects back to on success — typically
 *                 `/7days/success/{token}` or `/nabd/success/{token}`.
 *   theme         "gold" (7days) | "amber" (nabd).
 *   testid        Base data-testid for the form root.
 *   buttonLabel   Custom label override for the pay button.
 *   beforeConfirm Optional callback fired right before stripe.confirmPayment
 *                 — used for InitiateCheckout pixel tracking.
 *
 * Loads `@stripe/stripe-js` lazily (loadStripe). Renders the Stripe
 * Payment Element (`<PaymentElement>`) inside an `<Elements>` provider using
 * the `clientSecret` so Stripe scopes the element to that single PI.
 */

import { useEffect, useState, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Loader2, ShieldCheck, Lock } from "lucide-react";
import PaymentLogos from "./PaymentLogos";

// Read the publishable key from REACT_APP_*. Falls back to undefined if absent
// — the parent gracefully falls back to the hosted-checkout button in that case.
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
let _stripePromise = null;
const getStripe = () => {
  if (!STRIPE_PK) return null;
  if (!_stripePromise) _stripePromise = loadStripe(STRIPE_PK);
  return _stripePromise;
};

const THEMES = {
  gold: {
    bg: "#1A0F0F",
    border: "rgba(201,166,106,0.35)",
    accent: "#C9A66A",
    accentHover: "#D8B47A",
    text: "#F5EAD3",
    sub: "#A7956E",
    btnText: "#0A0606",
    appearance: {
      theme: "night",
      variables: {
        colorPrimary: "#C9A66A",
        colorBackground: "#1A0F0F",
        colorText: "#F5EAD3",
        colorDanger: "#E5484D",
        fontFamily: "Tajawal, system-ui, sans-serif",
        borderRadius: "12px",
        spacingUnit: "5px",
      },
      rules: {
        ".Input": {
          backgroundColor: "#0F0808",
          border: "1px solid rgba(201,166,106,0.25)",
          color: "#F5EAD3",
        },
        ".Input:focus": {
          border: "1px solid rgba(201,166,106,0.65)",
          boxShadow: "0 0 0 2px rgba(201,166,106,0.20)",
        },
        ".Label": { color: "#A7956E", fontWeight: "500" },
        ".Tab": { backgroundColor: "#0F0808", border: "1px solid rgba(201,166,106,0.20)", color: "#F5EAD3" },
        ".Tab--selected": { backgroundColor: "#1A0F0F", border: "1px solid #C9A66A" },
      },
    },
  },
  amber: {
    bg: "#0E1116",
    border: "rgba(244,184,66,0.35)",
    accent: "#F4B842",
    accentHover: "#FFC757",
    text: "#F0E9D6",
    sub: "#A89570",
    btnText: "#0A0E14",
    appearance: {
      theme: "night",
      variables: {
        colorPrimary: "#F4B842",
        colorBackground: "#0E1116",
        colorText: "#F0E9D6",
        colorDanger: "#E5484D",
        fontFamily: "Tajawal, system-ui, sans-serif",
        borderRadius: "12px",
        spacingUnit: "5px",
      },
      rules: {
        ".Input": {
          backgroundColor: "#070A0E",
          border: "1px solid rgba(244,184,66,0.22)",
          color: "#F0E9D6",
        },
        ".Input:focus": {
          border: "1px solid rgba(244,184,66,0.60)",
          boxShadow: "0 0 0 2px rgba(244,184,66,0.20)",
        },
        ".Label": { color: "#A89570", fontWeight: "500" },
        ".Tab": { backgroundColor: "#070A0E", border: "1px solid rgba(244,184,66,0.20)", color: "#F0E9D6" },
        ".Tab--selected": { backgroundColor: "#0E1116", border: "1px solid #F4B842" },
      },
    },
  },
  rose: {
    bg: "#1A0E22",
    border: "rgba(217,112,127,0.35)",
    accent: "#E8A18F",
    accentHover: "#F2BBA8",
    text: "#F6EFE7",
    sub: "#A795B8",
    btnText: "#1A0E22",
    appearance: {
      theme: "night",
      variables: {
        colorPrimary: "#E8A18F",
        colorBackground: "#1A0E22",
        colorText: "#F6EFE7",
        colorDanger: "#E5484D",
        fontFamily: "system-ui, -apple-system, sans-serif",
        borderRadius: "12px",
        spacingUnit: "5px",
      },
      rules: {
        ".Input": {
          backgroundColor: "#15081C",
          border: "1px solid rgba(217,112,127,0.22)",
          color: "#F6EFE7",
        },
        ".Input:focus": {
          border: "1px solid rgba(232,161,143,0.60)",
          boxShadow: "0 0 0 2px rgba(232,161,143,0.20)",
        },
        ".Label": { color: "#A795B8", fontWeight: "500" },
        ".Tab": { backgroundColor: "#15081C", border: "1px solid rgba(217,112,127,0.22)", color: "#F6EFE7" },
        ".Tab--selected": { backgroundColor: "#1A0E22", border: "1px solid #E8A18F" },
      },
    },
  },
};

// Inner form — must live INSIDE <Elements> to use the hooks.
const PaymentForm = ({ amount, currencyLabel, returnUrl, palette, theme, testid, buttonLabel, beforeConfirm, direction, trustText, processingText, defaultCountry }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      // beforeConfirm may now return `false` to abort the submit (e.g. when
      // the parent wants to show a soft "add email?" prompt). Treat thrown
      // errors as silent — they never block the funnel.
      if (typeof beforeConfirm === "function") {
        try {
          const result = await beforeConfirm();
          if (result === false) {
            setSubmitting(false);
            return;
          }
        } catch (_) { /* never blocks */ }
      }
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
      });
      if (stripeError) {
        setError(stripeError.message || (direction === "ltr"
          ? "Could not complete payment — please try again."
          : "تعذّر إتمام الدفع — حاولي مرة أخرى."));
      }
      // If no error and no redirect happened (rare), Stripe will redirect us
      // automatically to return_url; nothing further to do here.
    } catch (err) {
      setError(err?.message || (direction === "ltr" ? "Unexpected error." : "حدث خطأ غير متوقع."));
    } finally {
      setSubmitting(false);
    }
  };

  // PaymentElement options — pre-fill billing country so Gulf visitors don't
  // see "Egypt" auto-selected (Stripe's default fallback when IP geo is
  // ambiguous). Pre-filling the right country eliminated a critical
  // friction point that was killing Gulf conversion.
  const elementOptions = {
    layout: { type: "tabs", defaultCollapsed: false },
    paymentMethodOrder: ["apple_pay", "google_pay", "card", "link"],
    ...(defaultCountry ? {
      defaultValues: {
        billingDetails: {
          address: { country: defaultCountry },
        },
      },
    } : {}),
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" dir={direction || "rtl"} data-testid={`${testid}-form`}>
      <PaymentElement options={elementOptions} />

      {error && (
        <div
          className="rounded-xl p-3 text-[13px] font-semibold flex items-start gap-2"
          style={{ background: "rgba(229,72,77,0.10)", border: "1px solid rgba(229,72,77,0.35)", color: "#FFB4B7" }}
          data-testid={`${testid}-error`}
        >
          <span>{error}</span>
        </div>
      )}

      {/* Trust strip — official brand logos at uniform height. Premium look
          identical to Stripe Checkout / Shopify checkout. Lives right above
          the Pay button so it's the last visual signal before commitment. */}
      <PaymentLogos theme={theme} testid={`${testid}-trust-badges`} />

      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full font-extrabold text-[15px] rounded-2xl py-4 px-5 flex items-center justify-center gap-2 transition-transform active:scale-[0.99] disabled:opacity-60"
        style={{ background: palette.accent, color: palette.btnText, boxShadow: "0 8px 28px rgba(0,0,0,0.35)" }}
        data-testid={`${testid}-pay-button`}
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {processingText || (direction === "ltr" ? "Processing…" : "جاري المعالجة…")}
          </>
        ) : (
          <>
            <Lock className="w-4 h-4" />
            {buttonLabel || `${direction === "ltr" ? "Pay" : "ادفع الآن —"} ${amount} ${currencyLabel}`}
          </>
        )}
      </button>

      <div className="text-[11px] flex items-center justify-center gap-1.5" style={{ color: palette.sub }}>
        <ShieldCheck className="w-3 h-3" style={{ color: palette.accent }} />
        {trustText || (direction === "ltr"
          ? "Secure payment via Stripe · 7-day refund guarantee"
          : "دفع آمن عبر Stripe · ضمان استرداد ٧ أيام")}
      </div>
    </form>
  );
};

const InlinePayment = ({
  clientSecret,
  orderToken,
  amount,
  currency,
  currencyLabel,
  returnPath,
  theme = "gold",
  testid = "inline-payment",
  buttonLabel,
  beforeConfirm,
  direction,
  trustText,
  processingText,
  loadingText,
  unavailableText,
}) => {
  const palette = THEMES[theme] || THEMES.gold;
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeFail, setStripeFail] = useState(false);
  const stripePromiseRef = useRef(null);

  useEffect(() => {
    const p = getStripe();
    if (!p) { setStripeFail(true); return; }
    stripePromiseRef.current = p;
    p.then((s) => { if (s) setStripeReady(true); else setStripeFail(true); })
     .catch(() => setStripeFail(true));
  }, []);

  if (stripeFail || !STRIPE_PK) {
    return (
      <div
        className="rounded-2xl p-4 border text-[13px]"
        style={{ background: palette.bg, borderColor: palette.border, color: palette.text }}
        data-testid={`${testid}-stripe-missing`}
        dir={direction || "rtl"}
      >
        {unavailableText || (direction === "ltr"
          ? "Payment system is temporarily unavailable — please try again shortly."
          : "نظام الدفع غير متاح حاليًا — يرجى المحاولة بعد قليل.")}
      </div>
    );
  }

  if (!clientSecret || !stripeReady) {
    return (
      <div
        className="rounded-2xl p-6 border flex items-center justify-center gap-3 text-[13px]"
        style={{ background: palette.bg, borderColor: palette.border, color: palette.sub }}
        data-testid={`${testid}-loading`}
        dir={direction || "rtl"}
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        {loadingText || (direction === "ltr" ? "Preparing payment…" : "جاري تحضير نموذج الدفع…")}
      </div>
    );
  }

  const returnUrl = (() => {
    if (typeof window === "undefined") return returnPath;
    const base = window.location.origin;
    return `${base}${returnPath}`;
  })();

  return (
    <div
      className="rounded-2xl p-5 border"
      style={{ background: palette.bg, borderColor: palette.border }}
      data-testid={testid}
    >
      <Elements
        stripe={stripePromiseRef.current}
        options={{
          clientSecret,
          appearance: palette.appearance,
          locale: "auto",
        }}
      >
        <PaymentForm
          amount={amount}
          currencyLabel={currencyLabel || currency}
          returnUrl={returnUrl}
          palette={palette}
          theme={theme}
          testid={testid}
          buttonLabel={buttonLabel}
          beforeConfirm={beforeConfirm}
          direction={direction}
          trustText={trustText}
          processingText={processingText}
        />
      </Elements>
    </div>
  );
};

export default InlinePayment;
